import { buildListenUrl, CLOSE_STREAM, parseDeepgramMessage, socketProtocols } from "./deepgram";
import { browserMicrophone, MicrophoneError } from "./microphone";
import type {
  AudioCapture,
  DictationError,
  DictationSession,
  DictationSessionOptions,
  DictationStatus,
  WebSocketLike,
} from "./types";

const OPEN = 1;
const CLOSING = 2;

function describe(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * One dictation run: microphone → Deepgram streaming socket → segments. The microphone is
 * requested before a token is minted, so a denied permission never spends a token, and every
 * failure path releases the microphone and closes the socket.
 */
export function createDictationSession(options: DictationSessionOptions): DictationSession {
  const openMicrophone =
    options.openMicrophone ??
    (() => browserMicrophone(options.deviceId === undefined ? {} : { deviceId: options.deviceId }));
  const openSocket =
    options.openSocket ??
    ((url: string, protocols: string[]): WebSocketLike => new WebSocket(url, protocols));
  const schedule = options.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancel =
    options.clearTimeout ??
    ((handle: unknown) => clearTimeout(handle as Parameters<typeof clearTimeout>[0]));
  const closeTimeoutMs = options.closeTimeoutMs ?? 3000;

  let status: DictationStatus = "idle";
  let capture: AudioCapture | null = null;
  let socket: WebSocketLike | null = null;
  /** Bumped by `abort()` so a `start()` that is mid-await stops touching shared state. */
  let generation = 0;

  const setStatus = (next: DictationStatus, error?: DictationError) => {
    status = next;
    if (error) options.onStatus(next, error);
    else options.onStatus(next);
  };
  const release = async () => {
    const current = capture;
    capture = null;
    if (current) await current.close().catch(() => undefined);
  };
  const closeSocket = () => {
    const current = socket;
    socket = null;
    if (current && current.readyState < CLOSING) current.close();
  };
  const fail = (error: DictationError) => {
    void release();
    closeSocket();
    setStatus("error", error);
  };

  async function start(): Promise<void> {
    if (status !== "idle" && status !== "error") return;
    const run = ++generation;
    setStatus("requesting_mic");
    let mic: AudioCapture;
    try {
      mic = await openMicrophone();
    } catch (error) {
      if (run !== generation) return;
      setStatus("error", {
        reason: error instanceof MicrophoneError ? error.reason : "mic_unavailable",
        message: describe(error, "Could not open the microphone"),
        cause: error,
      });
      return;
    }
    if (run !== generation) {
      await mic.close().catch(() => undefined);
      return;
    }
    capture = mic;
    setStatus("connecting");
    let token: string;
    try {
      token = (await options.getToken()).token;
    } catch (error) {
      if (run !== generation) return;
      await release();
      setStatus("error", {
        reason: "token",
        message: describe(error, "Could not start dictation"),
        cause: error,
      });
      return;
    }
    if (run !== generation) return;
    let ws: WebSocketLike;
    try {
      ws = openSocket(
        buildListenUrl({
          sampleRate: mic.sampleRate,
          ...(options.language ? { language: options.language } : {}),
        }),
        socketProtocols(token),
      );
    } catch (error) {
      await release();
      setStatus("error", { reason: "network", message: describe(error, "Could not connect") });
      return;
    }
    socket = ws;
    let opened = false;
    ws.addEventListener("open", () => {
      if (socket !== ws) return;
      opened = true;
      setStatus("listening");
      mic.onChunk((pcm) => {
        if (socket === ws && ws.readyState === OPEN) ws.send(pcm);
      });
    });
    ws.addEventListener("message", (event) => {
      if (socket !== ws) return;
      const parsed = parseDeepgramMessage(event.data);
      if (!parsed) return;
      if (parsed.type === "error") fail({ reason: "provider", message: parsed.message });
      else options.onSegment(parsed.segment);
    });
    ws.addEventListener("error", () => undefined);
    ws.addEventListener("close", (event) => {
      if (socket !== ws || status === "stopping") return;
      socket = null;
      void release();
      setStatus("error", {
        reason: opened && event.code !== 1000 && event.code !== 1006 ? "provider" : "network",
        message:
          event.reason ||
          (opened ? "Dictation connection lost" : "Could not connect to the dictation service"),
      });
    });
  }

  async function stop(): Promise<void> {
    if (status === "requesting_mic" || status === "connecting") {
      abort();
      return;
    }
    if (status !== "listening") return;
    setStatus("stopping");
    const ws = socket;
    // Stop the microphone first so no audio follows the close request.
    await release();
    if (ws && ws.readyState === OPEN) {
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          cancel(timer);
          resolve();
        };
        const timer = schedule(finish, closeTimeoutMs);
        ws.addEventListener("close", finish);
        ws.send(CLOSE_STREAM);
      });
    }
    if (socket === ws) socket = null;
    if (ws && ws.readyState < CLOSING) ws.close();
    setStatus("idle");
  }

  function abort(): void {
    generation++;
    void release();
    closeSocket();
    if (status !== "idle") setStatus("idle");
  }

  return {
    get status() {
      return status;
    },
    start,
    stop,
    abort,
  };
}
